import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuBot,
  LuChevronLeft,
  LuChevronRight,
  LuDownload,
  LuEye,
  LuFileText,
  LuPaperclip,
  LuRefreshCw,
  LuSearch,
  LuUserCheck,
  LuUserCog,
  LuUserPlus,
  LuX,
} from "react-icons/lu";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import {
  assignPlatformDispute,
  disputeExtractMessage,
  fetchDisputeAssignees,
  fetchPlatformDisputeById,
  fetchPlatformDisputes,
} from "../../services/superAdminDispute.service";
import "../../assets/styles/super_admin_access.scss";

const PAGE_OPTIONS = [10, 20, 50];

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "new", label: "New" },
  { value: "assigned", label: "Assigned" },
  { value: "in_progress", label: "In Progress" },
  { value: "waiting_buyer", label: "Waiting for Buyer" },
  { value: "waiting_seller", label: "Waiting for Seller" },
  { value: "under_investigation", label: "Under Investigation" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
  { value: "reopened", label: "Reopened" },
];

const PRIORITY_OPTIONS = [
  { value: "", label: "All Priorities" },
  { value: "Low", label: "Low" },
  { value: "Medium", label: "Medium" },
  { value: "High", label: "High" },
  { value: "Urgent", label: "Urgent" },
];

const ASSIGNMENT_OPTIONS = [
  { value: "", label: "All Assignment Types" },
  { value: "unassigned", label: "Unassigned" },
  { value: "manual", label: "Manually Assigned" },
  { value: "automatic", label: "Automatically Assigned" },
];

const TAB_OPTIONS = [
  { id: "all", label: "All Disputes" },
  { id: "unassigned", label: "Unassigned" },
  { id: "assigned-to-me", label: "My Assigned Disputes", managerOnly: true },
];

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).replace(",", "");
}

function formatStatus(status) {
  return String(status || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatReferenceType(type) {
  const value = String(type || "").trim();
  if (!value) return "";
  const map = {
    rfq: "RFQ",
    "request for quotation (rfq)": "RFQ",
    "request for quotation": "RFQ",
    invoice: "Invoice",
    po: "PO",
    "purchase order": "PO",
    order: "Order",
  };
  return map[value.toLowerCase()] || value;
}

function truncateText(value, max = 28) {
  const text = String(value || "").trim();
  if (!text) return "—";
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

function nameInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "SU";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function assignmentBadge(type) {
  if (type === "manual") {
    return { label: "Manual", className: "sa-dispute-badge sa-dispute-badge--manual" };
  }
  if (type === "automatic") {
    return { label: "Auto", className: "sa-dispute-badge sa-dispute-badge--auto" };
  }
  return { label: "Unassigned", className: "sa-dispute-badge sa-dispute-badge--unassigned" };
}

function priorityBadge(priority) {
  const normalized = String(priority || "").toLowerCase();
  if (normalized === "urgent") return "sa-dispute-priority sa-dispute-priority--urgent";
  if (normalized === "high") return "sa-dispute-priority sa-dispute-priority--high";
  if (normalized === "medium") return "sa-dispute-priority sa-dispute-priority--medium";
  return "sa-dispute-priority sa-dispute-priority--low";
}

function statusBadge(status) {
  const normalized = String(status || "").toLowerCase();
  return `sa-dispute-status sa-dispute-status--${normalized.replace(/\s+/g, "_")}`;
}

function escapeCsv(value) {
  const text = String(value ?? "");
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function buildPageNumbers(currentPage, totalPages, maxVisible = 7) {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  start = Math.max(1, end - maxVisible + 1);
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

export default function SuperAdminDisputeManagementPage({
  basePath = "/super-admin",
  canAssign = false,
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState(() => searchParams.get("status") || "");
  const [priority, setPriority] = useState(() => searchParams.get("priority") || "");
  const [assignmentType, setAssignmentType] = useState(
    () => searchParams.get("assignmentType") || "",
  );
  const [assignedTo, setAssignedTo] = useState(() => searchParams.get("assignedTo") || "");
  const [assignedToSuperAdminId, setAssignedToSuperAdminId] = useState(
    () => searchParams.get("assignedToSuperAdminId") || "",
  );
  const [assignedToMe, setAssignedToMe] = useState(() => searchParams.get("assignedToMe") === "1");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [supportUsers, setSupportUsers] = useState([]);
  const [superAdmins, setSuperAdmins] = useState([]);
  const [globalStats, setGlobalStats] = useState({
    total: 0,
    unassigned: 0,
    manual: 0,
    automatic: 0,
  });
  const [viewDispute, setViewDispute] = useState(null);
  const [viewDisputeLoading, setViewDisputeLoading] = useState(false);
  const [viewDisputeError, setViewDisputeError] = useState("");
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignKey, setAssignKey] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const activeTab = useMemo(() => {
    if (assignedToMe) return "assigned-to-me";
    if (assignmentType === "unassigned") return "unassigned";
    return "all";
  }, [assignmentType, assignedToMe]);

  const supportManagerAssignees = useMemo(
    () =>
      supportUsers.filter(
        (user) =>
          user.accountType === "support_manager" ||
          user.account_type === "support_manager",
      ),
    [supportUsers],
  );
  const supportUserAssignees = useMemo(
    () =>
      supportUsers.filter(
        (user) =>
          user.accountType !== "support_manager" &&
          user.account_type !== "support_manager",
      ),
    [supportUsers],
  );

  const visibleTabs = useMemo(
    () => TAB_OPTIONS.filter((tab) => !tab.managerOnly || canAssign),
    [canAssign],
  );

  const assigneeFilterValue = assignedToMe
    ? "me"
    : assignedTo
      ? `support:${assignedTo}`
      : assignedToSuperAdminId
        ? `super_admin:${assignedToSuperAdminId}`
        : "";

  useEffect(() => {
    setAssignmentType(searchParams.get("assignmentType") || "");
    setStatus(searchParams.get("status") || "");
    setPriority(searchParams.get("priority") || "");
    setAssignedTo(searchParams.get("assignedTo") || "");
    setAssignedToSuperAdminId(searchParams.get("assignedToSuperAdminId") || "");
    setAssignedToMe(canAssign && searchParams.get("assignedToMe") === "1");
    setPage(1);
  }, [searchParams, canAssign]);

  useEffect(() => {
    if (!canAssign) {
      setSupportUsers([]);
      setSuperAdmins([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchDisputeAssignees();
        if (!cancelled) {
          setSupportUsers(Array.isArray(data.supportUsers) ? data.supportUsers : []);
          setSuperAdmins(Array.isArray(data.superAdmins) ? data.superAdmins : []);
        }
      } catch {
        if (!cancelled) {
          setSupportUsers([]);
          setSuperAdmins([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canAssign]);

  const loadGlobalStats = useCallback(async () => {
    try {
      const [allRes, unassignedRes, manualRes, autoRes] = await Promise.all([
        fetchPlatformDisputes({ page: 1, pageSize: 1 }),
        fetchPlatformDisputes({ assignmentType: "unassigned", page: 1, pageSize: 1 }),
        fetchPlatformDisputes({ assignmentType: "manual", page: 1, pageSize: 1 }),
        fetchPlatformDisputes({ assignmentType: "automatic", page: 1, pageSize: 1 }),
      ]);
      setGlobalStats({
        total: Math.trunc(Number(allRes.total) || 0),
        unassigned: Math.trunc(Number(unassignedRes.total) || 0),
        manual: Math.trunc(Number(manualRes.total) || 0),
        automatic: Math.trunc(Number(autoRes.total) || 0),
      });
    } catch {
      // Keep prior stats on failure.
    }
  }, []);

  useEffect(() => {
    loadGlobalStats();
  }, [loadGlobalStats]);

  const loadDisputes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPlatformDisputes({
        search: query.trim() || undefined,
        status: status || undefined,
        priority: priority || undefined,
        assignmentType: assignmentType || undefined,
        assignedTo: assignedTo || undefined,
        assignedToSuperAdminId: assignedToMe ? undefined : assignedToSuperAdminId || undefined,
        assignedToMe: assignedToMe ? "1" : undefined,
        page,
        pageSize,
      });
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Math.trunc(Number(data.total) || 0));
    } catch (error) {
      toast.error(disputeExtractMessage(error, "Failed to load disputes."));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [query, status, priority, assignmentType, assignedTo, assignedToSuperAdminId, assignedToMe, page, pageSize]);

  useEffect(() => {
    const timer = setTimeout(loadDisputes, 250);
    return () => clearTimeout(timer);
  }, [loadDisputes]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageNumbers = useMemo(
    () => buildPageNumbers(currentPage, totalPages),
    [currentPage, totalPages],
  );

  const syncUrlFilters = (next) => {
    const params = new URLSearchParams();
    if (next.assignmentType) params.set("assignmentType", next.assignmentType);
    if (next.status) params.set("status", next.status);
    if (next.priority) params.set("priority", next.priority);
    if (next.assignedTo) params.set("assignedTo", next.assignedTo);
    if (next.assignedToSuperAdminId) {
      params.set("assignedToSuperAdminId", next.assignedToSuperAdminId);
    }
    if (next.assignedToMe) params.set("assignedToMe", "1");
    setSearchParams(params, { replace: true });
  };

  const resetFilters = () => {
    setQuery("");
    setStatus("");
    setPriority("");
    setAssignmentType("");
    setAssignedTo("");
    setAssignedToSuperAdminId("");
    setAssignedToMe(false);
    setPage(1);
    setSearchParams({}, { replace: true });
  };

  const handleTabChange = (tabId) => {
    setPage(1);
    if (tabId === "all") {
      setAssignmentType("");
      setAssignedTo("");
      setAssignedToSuperAdminId("");
      setAssignedToMe(false);
      syncUrlFilters({ status, priority });
      return;
    }
    if (tabId === "unassigned") {
      setAssignmentType("unassigned");
      setAssignedTo("");
      setAssignedToSuperAdminId("");
      setAssignedToMe(false);
      syncUrlFilters({ assignmentType: "unassigned", status, priority });
      return;
    }
    if (tabId === "assigned-to-me") {
      if (!canAssign) return;
      setAssignmentType("");
      setAssignedTo("");
      setAssignedToSuperAdminId("");
      setAssignedToMe(true);
      syncUrlFilters({ assignedToMe: true, status, priority });
    }
  };

  const parseAssignKey = (value) => {
    const raw = String(value || "").trim();
    if (raw.startsWith("super_admin:")) {
      const id = Number(raw.slice("super_admin:".length));
      return { assigneeType: "super_admin", superAdminId: id, accessUserId: null };
    }
    if (raw.startsWith("support:")) {
      const id = Number(raw.slice("support:".length));
      return { assigneeType: "support", accessUserId: id, superAdminId: null };
    }
    const id = Number(raw);
    if (Number.isFinite(id) && id > 0) {
      return { assigneeType: "support", accessUserId: id, superAdminId: null };
    }
    return null;
  };

  const buildAssignKeyFromDispute = (dispute) => {
    if (!dispute) return "";
    if (dispute.assigneeKind === "super_admin" && dispute.assignedToSuperAdminId) {
      return `super_admin:${dispute.assignedToSuperAdminId}`;
    }
    if (dispute.assignedToAccessUserId) {
      return `support:${dispute.assignedToAccessUserId}`;
    }
    return "";
  };

  const handleView = (dispute) => {
    navigate(`${basePath}/disputes/${dispute.id}`);
  };

  const closeQuickView = () => {
    setViewDispute(null);
    setViewDisputeLoading(false);
    setViewDisputeError("");
  };

  const openQuickView = async (dispute) => {
    const disputeId = Number(dispute?.id);
    if (!Number.isFinite(disputeId) || disputeId <= 0) return;
    setViewDispute({ dispute, assignmentHistory: [] });
    setViewDisputeError("");
    setViewDisputeLoading(true);
    try {
      const detail = await fetchPlatformDisputeById(disputeId);
      if (detail?.dispute) {
        setViewDispute(detail);
      } else {
        setViewDisputeError("Dispute details are unavailable.");
      }
    } catch (error) {
      setViewDisputeError(disputeExtractMessage(error, "Failed to load dispute details."));
    } finally {
      setViewDisputeLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!assignTarget || !assignKey) {
      toast.error("Please select a support user or super admin.");
      return;
    }
    const parsed = parseAssignKey(assignKey);
    if (!parsed) {
      toast.error("Please select a valid assignee.");
      return;
    }
    setBusyId(assignTarget.id);
    try {
      const payload =
        parsed.assigneeType === "super_admin"
          ? { assigneeType: "super_admin", superAdminId: parsed.superAdminId }
          : { assigneeType: "support", accessUserId: parsed.accessUserId };
      await assignPlatformDispute(assignTarget.id, payload);
      toast.success(`Dispute ${assignTarget.disputeNumber} assigned successfully.`);
      setAssignTarget(null);
      setAssignKey("");
      await Promise.all([loadDisputes(), loadGlobalStats()]);
    } catch (error) {
      toast.error(disputeExtractMessage(error, "Failed to assign dispute."));
    } finally {
      setBusyId(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await fetchPlatformDisputes({
        search: query.trim() || undefined,
        status: status || undefined,
        priority: priority || undefined,
        assignmentType: assignmentType || undefined,
        assignedTo: assignedTo || undefined,
        assignedToSuperAdminId: assignedToMe ? undefined : assignedToSuperAdminId || undefined,
        assignedToMe: assignedToMe ? "1" : undefined,
        page: 1,
        pageSize: 1000,
      });
      const rows = Array.isArray(data.items) ? data.items : [];
      const headers = [
        "Dispute ID",
        "Internal ID",
        "Order Reference",
        "Reference Type",
        "Buyer",
        "Seller",
        "Priority",
        "Status",
        "Submitted",
        "Assignment",
        "Assignee",
        "Assignee Type",
        "Assignee Email",
      ];
      const csvLines = [
        headers.join(","),
        ...rows.map((dispute) =>
          [
            dispute.disputeNumber,
            dispute.id,
            dispute.referenceNumber || "",
            dispute.referenceType || "",
            dispute.buyerName,
            dispute.supplierName,
            dispute.priority,
            dispute.status,
            formatDateTime(dispute.createdAt),
            assignmentBadge(dispute.assignmentType).label,
            dispute.assignedToName || "",
            dispute.assigneeKind === "super_admin"
              ? "Super Admin"
              : dispute.assigneeKind === "support"
                ? "Support"
                : "",
            dispute.assignedToEmail || "",
          ]
            .map(escapeCsv)
            .join(","),
        ),
      ];
      const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "platform-disputes.csv";
      anchor.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} dispute${rows.length === 1 ? "" : "s"}.`);
    } catch (error) {
      toast.error(disputeExtractMessage(error, "Failed to export disputes."));
    } finally {
      setExporting(false);
    }
  };

  const refreshAll = async () => {
    await Promise.all([loadDisputes(), loadGlobalStats()]);
  };

  return (
    <div className="sa-access-page sa-dispute-page">
      <header className="sa-access-head">
        <div>
          <nav className="sa-access-breadcrumb" aria-label="Breadcrumb">
            <Link to={canAssign ? "/home" : "/super-admin/dashboard"}>Dashboard</Link>
            <span aria-hidden>&gt;</span>
            <span aria-current="page">Dispute Management</span>
          </nav>
          <h1>Dispute Management</h1>
          <p>
            {canAssign
              ? "Manage team assignments, workload, SLA, and dispute history."
              : "Read-only oversight of disputes, conversations, assignments, and audit history."}
          </p>
        </div>
        <div className="sa-access-actions">
          <button type="button" className="sa-access-btn sa-access-btn--ghost" onClick={refreshAll}>
            <LuRefreshCw aria-hidden /> Refresh
          </button>
        </div>
      </header>

      <section className="sa-access-stats">
        <article className="sa-access-stat">
          <span className="sa-access-stat__icon sa-access-stat__icon--blue">
            <LuFileText />
          </span>
          <div>
            <strong>{globalStats.total.toLocaleString()}</strong>
            <span>Total Disputes</span>
          </div>
        </article>
        <article className="sa-access-stat">
          <span className="sa-access-stat__icon sa-access-stat__icon--orange">
            <LuUserPlus />
          </span>
          <div>
            <strong>{globalStats.unassigned.toLocaleString()}</strong>
            <span>Unassigned</span>
          </div>
        </article>
        <article className="sa-access-stat">
          <span className="sa-access-stat__icon sa-access-stat__icon--green">
            <LuUserCheck />
          </span>
          <div>
            <strong>{globalStats.manual.toLocaleString()}</strong>
            <span>Manually Assigned</span>
          </div>
        </article>
        <article className="sa-access-stat">
          <span className="sa-access-stat__icon sa-access-stat__icon--purple">
            <LuBot />
          </span>
          <div>
            <strong>{globalStats.automatic.toLocaleString()}</strong>
            <span>Auto Assigned</span>
          </div>
        </article>
      </section>

      <div
        className="sa-access-toolbar sa-dispute-toolbar"
        style={{
          gridTemplateColumns: "minmax(220px, 1.6fr) repeat(4, minmax(140px, 1fr)) auto",
        }}
      >
        <label className="sa-access-search">
          <LuSearch aria-hidden />
          <input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
            placeholder="Search by Dispute ID, Order Ref, Buyer, Seller..."
          />
        </label>
        <select
          value={status}
          onChange={(event) => {
            const next = event.target.value;
            setStatus(next);
            setPage(1);
            syncUrlFilters({
              status: next,
              priority,
              assignmentType,
              assignedTo,
              assignedToSuperAdminId,
              assignedToMe,
            });
          }}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value || "all-status"} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={priority}
          onChange={(event) => {
            const next = event.target.value;
            setPriority(next);
            setPage(1);
            syncUrlFilters({
              status,
              priority: next,
              assignmentType,
              assignedTo,
              assignedToSuperAdminId,
              assignedToMe,
            });
          }}
        >
          {PRIORITY_OPTIONS.map((opt) => (
            <option key={opt.value || "all-priority"} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={assignmentType}
          onChange={(event) => {
            const next = event.target.value;
            setAssignmentType(next);
            setAssignedTo(next ? "" : assignedTo);
            setAssignedToSuperAdminId(next ? "" : assignedToSuperAdminId);
            setAssignedToMe(next ? false : assignedToMe);
            setPage(1);
            syncUrlFilters({
              status,
              priority,
              assignmentType: next,
              assignedTo: next ? "" : assignedTo,
              assignedToSuperAdminId: next ? "" : assignedToSuperAdminId,
              assignedToMe: next ? false : assignedToMe,
            });
          }}
        >
          {ASSIGNMENT_OPTIONS.map((opt) => (
            <option key={opt.value || "all-assignment"} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={assigneeFilterValue}
          onChange={(event) => {
            const next = event.target.value;
            setPage(1);
            if (!next) {
              setAssignedTo("");
              setAssignedToSuperAdminId("");
              setAssignedToMe(false);
              syncUrlFilters({ status, priority, assignmentType });
              return;
            }
            if (next === "me") {
              setAssignmentType("");
              setAssignedTo("");
              setAssignedToSuperAdminId("");
              setAssignedToMe(true);
              syncUrlFilters({ status, priority, assignedToMe: true });
              return;
            }
            if (next.startsWith("super_admin:")) {
              const id = next.slice("super_admin:".length);
              setAssignmentType("");
              setAssignedTo("");
              setAssignedToSuperAdminId(id);
              setAssignedToMe(false);
              syncUrlFilters({ status, priority, assignedToSuperAdminId: id });
              return;
            }
            const id = next.startsWith("support:") ? next.slice("support:".length) : next;
            setAssignmentType("");
            setAssignedTo(id);
            setAssignedToSuperAdminId("");
            setAssignedToMe(false);
            syncUrlFilters({ status, priority, assignedTo: id });
          }}
        >
          <option value="">All Assignees</option>
          {canAssign ? <option value="me">Assigned to Me</option> : null}
          {supportManagerAssignees.length ? (
            <optgroup label="Support Managers">
              {supportManagerAssignees.map((user) => (
                <option key={`manager-${user.id}`} value={`support:${user.id}`}>
                  {user.fullName}
                </option>
              ))}
            </optgroup>
          ) : null}
          <optgroup label="Support Users">
            {supportUserAssignees.map((user) => (
              <option key={`support-${user.id}`} value={`support:${user.id}`}>
                {user.fullName}
              </option>
            ))}
          </optgroup>
          {!canAssign || superAdmins.length ? <optgroup label="Super Admins">
            {superAdmins.map((admin) => (
              <option key={`sa-${admin.id}`} value={`super_admin:${admin.id}`}>
                {admin.fullName}
              </option>
            ))}
          </optgroup> : null}
        </select>
        <button type="button" className="sa-access-btn sa-access-btn--ghost" onClick={resetFilters}>
          Reset
        </button>
      </div>

      <section className="sa-access-card sa-dispute-card">
        <div className="sa-dispute-card-toolbar">
          <div className="sa-dispute-tabs" role="tablist" aria-label="Dispute views">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`sa-dispute-tabs__btn${activeTab === tab.id ? " sa-dispute-tabs__btn--active" : ""}`}
                onClick={() => handleTabChange(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="sa-dispute-card-toolbar__actions">
            <button
              type="button"
              className="sa-access-btn sa-access-btn--ghost"
              disabled={exporting}
              onClick={handleExport}
            >
              <LuDownload aria-hidden /> Export
            </button>
          </div>
        </div>

        <div className="sa-access-table-wrap">
          <table className="sa-access-table sa-dispute-table">
            <thead>
              <tr>
                <th>Dispute ID</th>
                <th>Order Reference</th>
                <th>Buyer</th>
                <th>Seller</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Assignment</th>
                <th>Assignee</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="sa-access-table__empty">
                    Loading disputes...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={10} className="sa-access-table__empty">
                    No disputes found.
                  </td>
                </tr>
              ) : (
                items.map((dispute) => {
                  const assignBadge = assignmentBadge(dispute.assignmentType);
                  const isUnassigned = dispute.assignmentType === "unassigned";
                  const canReassign =
                    !isUnassigned && String(dispute.status || "").toLowerCase() === "assigned";
                  return (
                    <tr key={dispute.id}>
                      <td>
                        <div className="sa-dispute-id-cell">
                          <button
                            type="button"
                            className="sa-dispute-id-link"
                            title={dispute.disputeNumber}
                            onClick={() => openQuickView(dispute)}
                          >
                            {dispute.disputeNumber}
                          </button>
                          <span className="sa-dispute-id-chip">
                            #{String(dispute.id).padStart(6, "0")}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="sa-dispute-stack">
                          <span className="sa-dispute-stack__title" title={dispute.referenceNumber || undefined}>
                            {truncateText(dispute.referenceNumber || "—", 22)}
                          </span>
                          {dispute.referenceType ? (
                            <span className="sa-dispute-stack__meta" title={dispute.referenceType}>
                              {formatReferenceType(dispute.referenceType)}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <div className="sa-dispute-stack">
                          <span className="sa-dispute-stack__title" title={dispute.buyerName}>
                            {truncateText(dispute.buyerName, 20)}
                          </span>
                          <span
                            className="sa-dispute-stack__meta"
                            title={dispute.buyerCompany || dispute.buyerEmail || undefined}
                          >
                            {truncateText(dispute.buyerCompany || dispute.buyerEmail || "—", 24)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="sa-dispute-stack">
                          <span className="sa-dispute-stack__title" title={dispute.supplierName}>
                            {truncateText(dispute.supplierName, 20)}
                          </span>
                          <span className="sa-dispute-stack__meta" title={dispute.supplierEmail || undefined}>
                            {truncateText(dispute.supplierEmail || "—", 24)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={priorityBadge(dispute.priority)}>{dispute.priority}</span>
                      </td>
                      <td>
                        <span className={statusBadge(dispute.status)}>{formatStatus(dispute.status)}</span>
                      </td>
                      <td>
                        <span className="sa-dispute-date">{formatDateTime(dispute.createdAt)}</span>
                      </td>
                      <td>
                        <span className={assignBadge.className}>{assignBadge.label}</span>
                      </td>
                      <td>
                        {dispute.assignedToName ? (
                          <div className="sa-dispute-support-user">
                            <span className="sa-dispute-avatar">{nameInitials(dispute.assignedToName)}</span>
                            <div className="sa-dispute-stack">
                              <span className="sa-dispute-stack__title" title={dispute.assignedToName}>
                                {truncateText(dispute.assignedToName, 18)}
                              </span>
                              <span className="sa-dispute-stack__meta" title={dispute.assignedToEmail || undefined}>
                                {dispute.assigneeKind === "super_admin"
                                  ? "Super Admin"
                                  : dispute.assigneeKind === "support"
                                    ? "Support"
                                    : truncateText(dispute.assignedToEmail || "—", 22)}
                                {dispute.assignedToEmail
                                  ? ` · ${truncateText(dispute.assignedToEmail, 18)}`
                                  : ""}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <span className="sa-dispute-stack__meta">—</span>
                        )}
                      </td>
                      <td>
                        <div className="sa-dispute-actions">
                          <button
                            type="button"
                            className="sa-dispute-icon-btn"
                            title="View details"
                            disabled={busyId === dispute.id}
                            onClick={() => handleView(dispute)}
                          >
                            <LuEye aria-hidden />
                          </button>
                          {canAssign && (isUnassigned || canReassign) ? (
                            <button
                              type="button"
                              className={`sa-dispute-icon-btn ${
                                isUnassigned
                                  ? "sa-dispute-icon-btn--assign"
                                  : "sa-dispute-icon-btn--reassign"
                              }`}
                              title={isUnassigned ? "Assign dispute" : "Reassign dispute"}
                              aria-label={
                                isUnassigned
                                  ? `Assign ${dispute.disputeNumber}`
                                  : `Reassign ${dispute.disputeNumber}`
                              }
                              disabled={busyId === dispute.id}
                              onClick={() => {
                                setAssignTarget(dispute);
                                setAssignKey(
                                  isUnassigned ? "" : buildAssignKeyFromDispute(dispute),
                                );
                              }}
                            >
                              {isUnassigned ? (
                                <LuUserPlus aria-hidden />
                              ) : (
                                <LuUserCog aria-hidden />
                              )}
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <footer className="sa-access-footer">
          <span>
            Showing {items.length ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
            {Math.min(currentPage * pageSize, total)} of {total} disputes
          </span>
          <div className="sa-access-pagination">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
            >
              <LuChevronLeft aria-hidden />
            </button>
            {pageNumbers.map((item) => (
              <button
                key={item}
                type="button"
                className={currentPage === item ? "is-active" : ""}
                onClick={() => setPage(item)}
              >
                {item}
              </button>
            ))}
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
            >
              <LuChevronRight aria-hidden />
            </button>
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
            >
              {PAGE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
          </div>
        </footer>
      </section>

      {viewDispute?.dispute
        ? createPortal(
            <div
              className="sa-access-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="sa-dispute-modal-title"
              onClick={closeQuickView}
            >
              <div
                className="sa-access-modal__panel sa-dispute-modal__panel"
                onClick={(event) => event.stopPropagation()}
              >
                <header className="sa-dispute-modal__head">
                  <div className="sa-dispute-modal__head-main">
                    <div className="sa-dispute-modal__title-row">
                      <h2 id="sa-dispute-modal-title">{viewDispute.dispute.disputeNumber}</h2>
                      <span className="sa-dispute-id-chip">
                        #{String(viewDispute.dispute.id).padStart(6, "0")}
                      </span>
                    </div>
                    <p className="sa-dispute-modal__subject">{viewDispute.dispute.subject}</p>
                    <div className="sa-dispute-modal__badges">
                      <span className={priorityBadge(viewDispute.dispute.priority)}>
                        {viewDispute.dispute.priority}
                      </span>
                      <span className={statusBadge(viewDispute.dispute.status)}>
                        {formatStatus(viewDispute.dispute.status)}
                      </span>
                      <span className={assignmentBadge(viewDispute.dispute.assignmentType).className}>
                        {assignmentBadge(viewDispute.dispute.assignmentType).label}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="sa-dispute-icon-btn"
                    aria-label="Close"
                    onClick={closeQuickView}
                  >
                    <LuX aria-hidden />
                  </button>
                </header>

                <div className="sa-dispute-modal__body">
                  {viewDisputeLoading ? (
                    <p className="sa-dispute-modal__description sa-dispute-modal__description--muted">
                      Loading dispute details...
                    </p>
                  ) : null}
                  {viewDisputeError ? (
                    <p className="sa-dispute-modal__description sa-dispute-modal__description--muted">
                      {viewDisputeError}
                    </p>
                  ) : null}
                  <section className="sa-dispute-modal__section">
                    <h3>Dispute Details</h3>
                    <div className="sa-dispute-modal__fields">
                      <div className="sa-dispute-modal__field">
                        <span>Type</span>
                        <strong>{viewDispute.dispute.disputeType || "—"}</strong>
                      </div>
                      <div className="sa-dispute-modal__field">
                        <span>Category</span>
                        <strong>{viewDispute.dispute.category || "—"}</strong>
                      </div>
                      <div className="sa-dispute-modal__field">
                        <span>Reference</span>
                        <strong>
                          {viewDispute.dispute.referenceNumber || "—"}
                          {viewDispute.dispute.referenceType
                            ? ` · ${formatReferenceType(viewDispute.dispute.referenceType)}`
                            : ""}
                        </strong>
                      </div>
                      <div className="sa-dispute-modal__field">
                        <span>Submitted</span>
                        <strong>{formatDateTime(viewDispute.dispute.createdAt)}</strong>
                      </div>
                    </div>
                  </section>

                  <section className="sa-dispute-modal__section">
                    <h3>Description</h3>
                    <p className="sa-dispute-modal__description">
                      {viewDispute.dispute.description || "No description provided."}
                    </p>
                  </section>

                  <section className="sa-dispute-modal__section">
                    <h3>Attachment</h3>
                    {viewDispute.dispute.attachmentPath || viewDispute.dispute.attachmentName ? (
                      <a
                        className="sa-dispute-attachment"
                        href={resolvePublicFileUrl(viewDispute.dispute.attachmentPath)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span className="sa-dispute-attachment__icon" aria-hidden>
                          <LuPaperclip />
                        </span>
                        <span className="sa-dispute-attachment__meta">
                          <strong>
                            {viewDispute.dispute.attachmentName || "View attachment"}
                          </strong>
                          <small>Open / download file</small>
                        </span>
                      </a>
                    ) : (
                      <p className="sa-dispute-modal__description sa-dispute-modal__description--muted">
                        No attachment submitted with this dispute.
                      </p>
                    )}
                  </section>

                  <section className="sa-dispute-modal__section">
                    <h3>Parties</h3>
                    <div className="sa-dispute-modal__parties">
                      <article className="sa-dispute-party-card">
                        <span className="sa-dispute-party-card__label">Buyer</span>
                        <div className="sa-dispute-support-user">
                          <span className="sa-dispute-avatar">
                            {nameInitials(viewDispute.dispute.buyerName)}
                          </span>
                          <div className="sa-dispute-stack">
                            <span className="sa-dispute-stack__title">{viewDispute.dispute.buyerName}</span>
                            <span className="sa-dispute-stack__meta">
                              {viewDispute.dispute.buyerCompany || viewDispute.dispute.buyerEmail}
                            </span>
                            {viewDispute.dispute.buyerCompany ? (
                              <span className="sa-dispute-stack__meta">{viewDispute.dispute.buyerEmail}</span>
                            ) : null}
                          </div>
                        </div>
                      </article>
                      <article className="sa-dispute-party-card">
                        <span className="sa-dispute-party-card__label">Seller</span>
                        <div className="sa-dispute-support-user">
                          <span className="sa-dispute-avatar sa-dispute-avatar--seller">
                            {nameInitials(viewDispute.dispute.supplierName)}
                          </span>
                          <div className="sa-dispute-stack">
                            <span className="sa-dispute-stack__title">{viewDispute.dispute.supplierName}</span>
                            <span className="sa-dispute-stack__meta">
                              {viewDispute.dispute.supplierEmail || "—"}
                            </span>
                          </div>
                        </div>
                      </article>
                    </div>
                  </section>

                  <section className="sa-dispute-modal__section">
                    <h3>Assignment</h3>
                    <div className="sa-dispute-modal__fields">
                      <div className="sa-dispute-modal__field">
                        <span>Assignee</span>
                        <strong>
                          {viewDispute.dispute.assignedToName || "Unassigned"}
                          {viewDispute.dispute.assigneeKind === "super_admin"
                            ? " (Super Admin)"
                            : viewDispute.dispute.assigneeKind === "support"
                              ? " (Support)"
                              : ""}
                          {viewDispute.dispute.assignedToEmail
                            ? ` · ${viewDispute.dispute.assignedToEmail}`
                            : ""}
                        </strong>
                      </div>
                      <div className="sa-dispute-modal__field">
                        <span>Assigned At</span>
                        <strong>{formatDateTime(viewDispute.dispute.assignedAt)}</strong>
                      </div>
                    </div>
                  </section>

                  {Array.isArray(viewDispute.assignmentHistory) &&
                  viewDispute.assignmentHistory.length > 0 ? (
                    <section className="sa-dispute-modal__section">
                      <h3>Assignment History</h3>
                      <ul className="sa-dispute-timeline">
                        {viewDispute.assignmentHistory.map((entry) => (
                          <li key={entry.id}>
                            <span
                              className={
                                assignmentBadge(entry.assignmentType === "manual" ? "manual" : "automatic")
                                  .className
                              }
                            >
                              {entry.assignmentType === "manual" ? "Manual" : "Auto"}
                            </span>
                            <div>
                              <strong>
                                {entry.assignedToName
                                  || (entry.assignedToSuperAdminId
                                    ? `Super Admin #${entry.assignedToSuperAdminId}`
                                    : entry.assignedToAccessUserId
                                      ? `User #${entry.assignedToAccessUserId}`
                                      : "—")}
                                {entry.assigneeKind === "super_admin"
                                  ? " · Super Admin"
                                  : entry.assigneeKind === "support"
                                    ? " · Support"
                                    : ""}
                              </strong>
                              <small>
                                {formatDateTime(entry.createdAt)}
                                {entry.assignedBySuperAdminName
                                  ? ` · by ${entry.assignedBySuperAdminName}`
                                  : ""}
                              </small>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </section>
                  ) : null}
                </div>

                <footer className="sa-access-modal__footer sa-dispute-modal__footer">
                  <button
                    type="button"
                    className="sa-access-btn sa-access-btn--ghost"
                    onClick={closeQuickView}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="sa-access-btn sa-access-btn--ghost"
                    onClick={() => {
                      closeQuickView();
                      handleView(viewDispute.dispute);
                    }}
                  >
                    Open full details
                  </button>
                  {canAssign && viewDispute.dispute.assignmentType === "unassigned" ? (
                    <button
                      type="button"
                      className="sa-access-btn sa-access-btn--primary"
                      onClick={() => {
                        setAssignTarget(viewDispute.dispute);
                        setAssignKey("");
                        closeQuickView();
                      }}
                    >
                      Assign
                    </button>
                  ) : canAssign &&
                    String(viewDispute.dispute.status || "").toLowerCase() === "assigned" ? (
                    <button
                      type="button"
                      className="sa-access-btn sa-access-btn--primary"
                      onClick={() => {
                        setAssignTarget(viewDispute.dispute);
                        setAssignKey(buildAssignKeyFromDispute(viewDispute.dispute));
                        closeQuickView();
                      }}
                    >
                      Reassign
                    </button>
                  ) : null}
                </footer>
              </div>
            </div>,
            document.body,
          )
        : null}

      {assignTarget
        ? createPortal(
            <div
              className="sa-access-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="sa-dispute-assign-title"
              onClick={() => setAssignTarget(null)}
            >
              <div
                className="sa-access-modal__panel sa-dispute-assign-modal"
                onClick={(event) => event.stopPropagation()}
              >
                <header className="sa-dispute-assign-modal__head">
                  <div className="sa-dispute-assign-modal__title-wrap">
                    <span
                      className={`sa-dispute-assign-modal__icon ${
                        assignTarget.assignmentType !== "unassigned"
                          ? "sa-dispute-assign-modal__icon--reassign"
                          : "sa-dispute-assign-modal__icon--assign"
                      }`}
                      aria-hidden
                    >
                      {assignTarget.assignmentType !== "unassigned" ? (
                        <LuUserCog />
                      ) : (
                        <LuUserPlus />
                      )}
                    </span>
                    <div className="sa-dispute-modal__head-main">
                      <h2 id="sa-dispute-assign-title">
                        {assignTarget.assignmentType !== "unassigned"
                          ? "Reassign Dispute"
                          : "Assign Dispute"}
                      </h2>
                      <p className="sa-dispute-assign-modal__lead">
                        {canAssign
                          ? "Choose an available Support Manager or Support User from your team."
                          : "Choose an active, available Support User from your team."}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="sa-dispute-icon-btn"
                    aria-label="Close"
                    onClick={() => setAssignTarget(null)}
                  >
                    <LuX aria-hidden />
                  </button>
                </header>

                <div className="sa-dispute-assign-modal__body">
                  <div className="sa-dispute-assign-modal__summary">
                    <div className="sa-dispute-assign-modal__summary-top">
                      <strong>{assignTarget.disputeNumber}</strong>
                      <span className={assignmentBadge(assignTarget.assignmentType).className}>
                        {assignmentBadge(assignTarget.assignmentType).label}
                      </span>
                      {assignTarget.priority ? (
                        <span className={priorityBadge(assignTarget.priority)}>
                          {assignTarget.priority}
                        </span>
                      ) : null}
                    </div>
                    <p title={assignTarget.subject || undefined}>
                      {assignTarget.subject || "No subject provided"}
                    </p>
                    {assignTarget.assignmentType !== "unassigned" && assignTarget.assignedToName ? (
                      <small>
                        Currently assigned to{" "}
                        <em>
                          {assignTarget.assignedToName}
                          {assignTarget.assigneeKind === "super_admin"
                            ? " (Super Admin)"
                            : " (Support)"}
                        </em>
                      </small>
                    ) : null}
                  </div>

                  <label className="sa-access-field sa-dispute-assign-modal__field">
                    <span>
                      Assign To <em>*</em>
                    </span>
                    <select
                      value={assignKey}
                      onChange={(event) => setAssignKey(event.target.value)}
                    >
                      <option value="">Select an assignee</option>
                      {supportManagerAssignees.length ? (
                        <optgroup label="Support Managers">
                          {supportManagerAssignees.map((user) => (
                            <option
                              key={`assign-manager-${user.id}`}
                              value={`support:${user.id}`}
                            >
                              {user.fullName} ({user.email})
                              {user.workload ? ` · ${user.workload.active} active` : ""}
                            </option>
                          ))}
                        </optgroup>
                      ) : null}
                      <optgroup label="Support Users">
                        {supportUserAssignees.map((user) => (
                          <option key={`assign-support-${user.id}`} value={`support:${user.id}`}>
                            {user.fullName} ({user.email})
                            {user.workload ? ` · ${user.workload.active} active` : ""}
                          </option>
                        ))}
                      </optgroup>
                      {!canAssign || superAdmins.length ? <optgroup label="Super Admins">
                        {superAdmins.map((admin) => (
                          <option key={`assign-sa-${admin.id}`} value={`super_admin:${admin.id}`}>
                            {admin.fullName} ({admin.email})
                          </option>
                        ))}
                      </optgroup> : null}
                    </select>
                  </label>

                  <div className="sa-dispute-assign-modal__note" role="note">
                    <LuBot aria-hidden />
                    <p>
                      If still unassigned after 5 minutes, the system auto-assigns among available
                      support users only.
                    </p>
                  </div>
                </div>

                <footer className="sa-dispute-assign-modal__footer">
                  <button
                    type="button"
                    className="sa-access-btn sa-access-btn--ghost"
                    onClick={() => setAssignTarget(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="sa-access-btn sa-access-btn--primary"
                    disabled={busyId === assignTarget.id || !assignKey}
                    onClick={handleAssign}
                  >
                    {assignTarget.assignmentType !== "unassigned" ? (
                      <>
                        <LuUserCog aria-hidden /> Reassign
                      </>
                    ) : (
                      <>
                        <LuUserPlus aria-hidden /> Assign Dispute
                      </>
                    )}
                  </button>
                </footer>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
